import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import _ from 'underscore';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';

import { FilePicker } from './file-picker';
import { readFileAsDataURLAsync } from '../utils/read-file';
import { MaximumImageFileSizeMB } from '../utils/constants';

// Refer to https://docs.microsoft.com/en-us/azure/media-services/latest/encode-media-encoder-standard-formats-reference
const defaultAllowedImageExtensions = ['.png', '.jpg', '.jpeg', '.gif'];

// to reuse existing test coverage, still keep class format
export class ImageFilePicker extends React.PureComponent {
  onFileReadInternal = (selectedFiles) => {
    const { onFileRead } = this.props;

    return Promise.map(
      selectedFiles,
      fileObj => readFileAsDataURLAsync(fileObj)
    ).then((imageSources) => {
      onFileRead(imageSources, selectedFiles);
    });
  };

  getReachFileSizeLimitErrorMessage = ({ fileSizeMB, maximumFileSizeMB }) => {
    const { enableMultiple, i18n } = this.props;
    let errorMsg;

    if (enableMultiple) {
      errorMsg = i18n.getString('ImageExperience_MultipleUpload_FileSize_TooBig', {
        maximumFileSize: maximumFileSizeMB,
      });
    } else {
      errorMsg = i18n.getString('ImageExperience_FileSize_Too_Big_Template', {
        fileSize: fileSizeMB,
        maximumFileSize: maximumFileSizeMB,
      });
    }

    return errorMsg;
  };

  showExtensionError = () => {
    const {
      i18n, showErrors, enableMultiple, isMultimediaAd, isAudienceAd,
    } = this.props;

    let errorLinkText;
    let errorTopic;

    if (isMultimediaAd) {
      errorLinkText = i18n.getString(_TL_('About multimedia ads'));
      errorTopic = 'app54810';
    } else if (isAudienceAd) {
      errorLinkText = i18n.getString('ImageExperience_BestPracticeHelp');
      errorTopic = 'app54718';
    } else {
      errorLinkText = i18n.getString(_TL_('About image guidelines and policies'));
      errorTopic = 'app54718';
    }

    showErrors([
      <span>
        {i18n.getString(`ImageExperience_${enableMultiple ? 'MultipleUpload_' : ''}Error_MediaFormatNotSupported`)}
        <LearnMoreLink
          linkText={errorLinkText}
          topic={errorTopic}
        />
      </span>,
    ]);
  };

  render() {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onFileRead, minimumSize, maxUploadNum, ...rest
    } = this.props;
    const { i18n } = rest;
    let fileDraggerTip = maxUploadNum >= 0 ? i18n.getString('ImageExperience_Drag_File_Maximum_Upload_Num', { maxCount: maxUploadNum }) : '';
    if (!_.isUndefined(minimumSize) && _.isArray(minimumSize) && minimumSize.length > 1) {
      if (fileDraggerTip) {
        fileDraggerTip += ' ';
      }
      fileDraggerTip += i18n.getString('ImageExperience_Drag_File_Minimum_Size_Tip', { width: minimumSize[0], height: minimumSize[1] });
    }

    return (
      <FilePicker
        allowedFileExtensions={defaultAllowedImageExtensions}
        onFileRead={this.onFileReadInternal}
        maximumFileSizeMB={MaximumImageFileSizeMB}
        getReachFileSizeLimitErrorMessage={this.getReachFileSizeLimitErrorMessage}
        onInvalidExtension={this.onInvalidExtension}
        showExtensionError={this.showExtensionError}
        fileDraggerLabel={i18n.getString('ImageExperience_Drag_File_Description')}
        fileDraggerTip={fileDraggerTip}
        fileDraggerDropDescription={i18n.getString('ImageExperience_Drop_Image_Description')}
        {...rest}
      />
    );
  }
}

ImageFilePicker.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  onFileRead: PropTypes.func.isRequired,
  isMultimediaAd: PropTypes.bool,
  isAudienceAd: PropTypes.bool,
  showErrors: PropTypes.func.isRequired,
  minimumSize: PropTypes.arrayOf(PropTypes.number),
  maxUploadNum: PropTypes.number,
  enableMultiple: PropTypes.bool,
};

ImageFilePicker.defaultProps = {
  isMultimediaAd: false,
  isAudienceAd: false,
  minimumSize: [],
  maxUploadNum: -1,
  enableMultiple: false,
};
